/**
 * 截图功能 composable
 */
import { ref } from 'vue';
import html2canvas from 'html2canvas';
import L from 'leaflet';
import { ElMessage } from 'element-plus';

export function useScreenshot(mapRef) {
  const isCapturing = ref(false);

  /**
   * 等待地图所有瓦片图层加载完成
   */
  const waitForTilesLoaded = () => {
    return new Promise((resolve) => {
      if (!mapRef.value) return resolve();
      const tileLayers = [];
      mapRef.value.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) tileLayers.push(layer);
      });
      if (tileLayers.length === 0) return resolve();

      const pendings = tileLayers.filter((l) => l._loading);
      if (pendings.length === 0) return resolve();

      let remaining = pendings.length;
      const timer = setTimeout(() => resolve(), 5000);
      pendings.forEach((layer) => {
        layer.once('load', () => {
          remaining -= 1;
          if (remaining <= 0) {
            clearTimeout(timer);
            resolve();
          }
        });
      });
    });
  };

  /**
   * 等待两帧，确保 Canvas/SVG renderer 完成绘制
   */
  const waitForNextPaint = () => {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  };

  /**
   * 执行截图
   * @param {string} filename - 截图文件名前缀
   */
  const takeScreenshot = async (filename = '地图截图') => {
    const mapContainer = document.getElementById('map');
    isCapturing.value = true;
    if (!mapContainer) {
      ElMessage.error('地图容器未找到');
      isCapturing.value = false;
      return;
    }

    try {
      if (mapRef.value && mapRef.value.invalidateSize) {
        mapRef.value.invalidateSize(false);
      }
      if (mapRef.value && mapRef.value.whenReady) {
        await new Promise((resolve) => mapRef.value.whenReady(resolve));
      }
      await waitForTilesLoaded();
      await waitForNextPaint();

      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0b0f39',
        logging: false,
        width: mapContainer.offsetWidth,
        height: mapContainer.offsetHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      ElMessage.success('截图已保存');
    } catch (err) {
      console.error('截图失败:', err);
      ElMessage.error('截图失败，请重试');
    } finally {
      isCapturing.value = false;
    }
  };

  return { takeScreenshot, isCapturing };
}
